import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import {
  NotificationsApi,
  NotificationsControllerGetNotificationsEventEnum,
  type NotificationUpdateCommand,
} from "@/config/apiClient";
import { createApiClient } from "@/config/createApiClient";
import { currentWorkspaceAtom } from "@/store";

const apiClient = createApiClient(NotificationsApi);

interface QueryGetNotifications {
  page: number;
  size: number;
  receivedAt?: string;
}

export const useNotifications = () => {
  const currentWorkspace = useAtomValue(currentWorkspaceAtom);

  const queryClient = useQueryClient();

  const useGetNotifications = (params: QueryGetNotifications) =>
    useQuery({
      staleTime: 30 * 60 * 1000,
      queryKey: [
        "notifications",
        params.page,
        params.size,
        params.receivedAt,
        currentWorkspace?.accountId,
        currentWorkspace?.organizationId,
        currentWorkspace?.id,
      ],
      enabled: !!currentWorkspace,
      queryFn: () => {
        return apiClient.notificationsControllerGetNotifications({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          page: params.page,
          size: params.size,
          receivedAt: params.receivedAt,
          event:
            NotificationsControllerGetNotificationsEventEnum.OrderLogCommentCreated,
        });
      },
    });

  const useGetLatestNotificationsByGroups = (params: QueryGetNotifications) =>
    useQuery({
      staleTime: 30 * 60 * 1000,
      queryKey: [
        "notifications",
        params.page,
        params.size,
        params.receivedAt,
        currentWorkspace?.accountId,
        currentWorkspace?.organizationId,
        currentWorkspace?.id,
      ],
      enabled: !!currentWorkspace,
      queryFn: () => {
        return apiClient.notificationsControllerGetLatestNotificationsByGroups({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          page: params.page,
          size: params.size,
          receivedAt: params.receivedAt,
          event:
            NotificationsControllerGetNotificationsEventEnum.OrderLogCommentCreated,
        });
      },
    });

  const useUpdateNotification = () =>
    useMutation<
      Awaited<ReturnType<typeof apiClient.notificationsControllerUpdate>>,
      Error,
      {
        notificationId: string;
        notificationUpdateCommand: NotificationUpdateCommand;
      }
    >({
      mutationKey: ["updateNotification"],
      mutationFn: (params) =>
        apiClient.notificationsControllerUpdate({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          notificationUpdateCommand: params.notificationUpdateCommand,
          id: params.notificationId,
        }),
      onSuccess: () => {
        return queryClient.invalidateQueries({
          queryKey: ["notifications"],
        });
      },
    });

  return {
    useGetNotifications,
    useGetLatestNotificationsByGroups,
    useUpdateNotification,
  };
};
